import { Mail, RefreshCw } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import AuthLayout from '../components/AuthLayout';
import { VerificationCodeInput } from '../components/forms';
import { authService } from '../services/authService';
import { maskEmail } from '../utils/authUtils';

interface ResetPasswordOTPFormData {
  otp: string;
}

const ResetPasswordOTP: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendMessage, setResendMessage] = useState<string | null>(null);

  // Get email from navigation state
  const email = location.state?.email;

  useEffect(() => {
    // If no email in state, redirect to forgot password
    if (!email) {
      navigate('/forgot-password');
    }
  }, [email, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<ResetPasswordOTPFormData>();

  const onSubmit = async (data: ResetPasswordOTPFormData) => {
    if (!email) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.verifyResetPasswordOTP(email, data.otp);
      if (response.success) {
        // Redirect to reset password page with email and OTP
        navigate('/reset-password', {
          state: {
            email,
            otp: data.otp,
            verified: true,
          },
        });
      } else {
        setError(response.message || t('auth.invalidOrExpiredCode'));
      }
    } catch {
      setError(t('auth.anErrorOccurred'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!email) return;

    setIsResending(true);
    setError(null);
    setResendMessage(null);

    try {
      const response = await authService.forgotPassword({ email });
      if (response.success) {
        setResendMessage(t('auth.resendCode'));
      } else {
        setError(response.message || t('auth.failedToResendCode'));
      }
    } catch {
      setError(t('auth.failedToResendCode'));
    } finally {
      setIsResending(false);
    }
  };

  if (!email) {
    return null; // Will redirect in useEffect
  }

  return (
    <AuthLayout
      title={t('auth.enterResetCode')}
      subtitle={`${t('auth.weSentDigitCodeTo')} ${maskEmail(email)}`}
      backTo="/forgot-password"
    >
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {resendMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-600">{resendMessage}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* OTP field */}
        <VerificationCodeInput
          {...register('otp', {
            required: t('auth.errors.verificationCodeRequired'),
            pattern: {
              value: /^\d{6}$/,
              message: t('auth.errors.enterSixDigitCode'),
            },
          })}
          length={6}
          error={errors.otp?.message}
          onChange={value => setValue('otp', value)}
        />

        {/* Submit button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-brand-primary hover:bg-brand-primary-hover text-white font-bold py-[14px] px-[30px] rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm leading-[1.2102272851126534em] uppercase tracking-wide"
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              <span>{t('auth.verifyingCode')}</span>
            </div>
          ) : (
            t('auth.verifyCode').toUpperCase()
          )}
        </button>

        {/* Resend code section */}
        <div className="text-center space-y-3">
          <p className="text-base font-normal text-[#1F1F1F] leading-[1.75em]">
            {t('auth.didntReceiveCode')}
          </p>

          <button
            type="button"
            onClick={handleResendOTP}
            disabled={isResending}
            className="inline-flex items-center text-base font-medium text-brand-primary hover:text-brand-primary-hover transition-colors disabled:opacity-50"
          >
            {isResending ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                {t('auth.sending')}
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                {t('auth.resendCode')}
              </>
            )}
          </button>

          <div className="mt-4">
            <Link
              to="/forgot-password"
              className="text-base font-normal text-[#6E7077] hover:text-[#1F1F1F] transition-colors leading-[1.75em]"
            >
              {t('auth.useOtherEmail')}
            </Link>
          </div>
        </div>
      </form>
    </AuthLayout>
  );
};

export default ResetPasswordOTP;
